<script lang="ts">
  import { formatNumberWithCommas } from "$lib/utils/number";

  /**
   * UI component for pagination controls
   *
   * This component renders pagination controls (page size selector, navigation buttons)
   * based on the current pagination state.
   */

  let {
    currentPage = 1,
    pageSize = 25,
    pageSizeOptions = [25, 50, 100],
    totalItems = 0,
    onPageChange = (direction: "next" | "prev") => {},
    onPageSizeChange = (newSize: number) => {},
  } = $props<{
    /** Current page number */
    currentPage: number;
    /** Current page size */
    pageSize: number;
    /** Available page size options */
    pageSizeOptions: number[];
    /** Total number of items */
    totalItems: number;
    /** Callback for page navigation */
    onPageChange: (direction: "next" | "prev") => void;
    /** Callback for page size changes */
    onPageSizeChange: (newSize: number) => void;
  }>();
  const hasPrevious = $derived(currentPage > 1);
  const hasNext = $derived(currentPage < totalItems / pageSize);

  // Dropdown arrow style
  const selectStyle =
    "background-image: url('data:image/svg+xml;charset=utf-8,%3Csvg xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22 width%3D%2212%22 height%3D%2212%22 viewBox%3D%220 0 24 24%22 fill%3D%22none%22 stroke%3D%22currentColor%22 stroke-width%3D%222%22 stroke-linecap%3D%22round%22 stroke-linejoin%3D%22round%22%3E%3Cpath d%3D%22m6 9 6 6 6-6%22%2F%3E%3C%2Fsvg%3E'); background-repeat: no-repeat; background-position: right 2px center;";
</script>

<div class="flex items-center justify-between text-sm gap-6">
  <div class="pl-2">
    <span class="text-offblack-900">
      {formatNumberWithCommas(currentPage - 1) * pageSize + 1} - {formatNumberWithCommas(
        Math.min(currentPage * pageSize, totalItems),
      )}
      of {formatNumberWithCommas(totalItems)} items
    </span>
  </div>
  <div class="flex-1 flex justify-end gap-6">
    <div class="flex items-center gap-2">
      <span class="text-offblack-900">Rows per page</span>
      <select
        value={pageSize}
        onchange={(e) => onPageSizeChange(parseInt(e.currentTarget.value))}
        class="bg-white text-offblack-900 cursor-pointer appearance-none pl-1 pr-5 py-0.5 min-w-[40px] text-center"
        style={selectStyle}
      >
        {#each pageSizeOptions as size}
          <option value={size}>{size}</option>
        {/each}
      </select>
    </div>

    <div class="flex items-center gap-4">
      <span class="text-offblack-900">
        Page {currentPage}
      </span>

      <div class="flex items-center">
        <button
          disabled={!hasPrevious}
          onclick={() => onPageChange("prev")}
          class="p-1 text-offblack-900 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Previous page"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>

        <button
          disabled={!hasNext}
          onclick={() => onPageChange("next")}
          class="p-1 text-offblack-900 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Next page"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      </div>
    </div>
  </div>
</div>
